import React, { useState } from "react";
import { ClassicLadderMatch } from "../types/tournament";
import { validateScore, isGameComplete } from "../utils/tournamentLogic";
import {
  X,
  Trophy,
  AlertCircle,
  Crown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

interface ClassicLadderScoreEntryProps {
  match: ClassicLadderMatch;
  onClose: () => void;
  onSubmit: (matchId: string, team1Score: number, team2Score: number) => void;
}

const ClassicLadderScoreEntry: React.FC<ClassicLadderScoreEntryProps> = ({
  match,
  onClose,
  onSubmit,
}) => {
  const [team1Score, setTeam1Score] = useState("");
  const [team2Score, setTeam2Score] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const score1 = parseInt(team1Score);
    const score2 = parseInt(team2Score);

    // Validation
    if (!validateScore(score1) || !validateScore(score2)) {
      setError("Scores must be between 0 and 13");
      return;
    }

    if (!isGameComplete(score1, score2)) {
      setError(
        "Game must be complete (one team must reach 11+ points and win by 2, or reach 13 points)"
      );
      return;
    }

    if (score1 === score2) {
      setError("Scores cannot be tied");
      return;
    }

    setError("");
    onSubmit(match.id, score1, score2);
    onClose();
  };

  const getPotentialWinner = () => {
    const score1 = parseInt(team1Score) || 0;
    const score2 = parseInt(team2Score) || 0;

    if (score1 > score2 && isGameComplete(score1, score2)) {
      return { team: match.team1, movement: "UP" };
    } else if (score2 > score1 && isGameComplete(score1, score2)) {
      return { team: match.team2, movement: "UP" };
    }
    return null;
  };

  const getPotentialLoser = () => {
    const score1 = parseInt(team1Score) || 0;
    const score2 = parseInt(team2Score) || 0;

    if (score1 > score2 && isGameComplete(score1, score2)) {
      return { team: match.team2, movement: "DOWN" };
    } else if (score2 > score1 && isGameComplete(score1, score2)) {
      return { team: match.team1, movement: "DOWN" };
    }
    return null;
  };

  const potentialWinner = getPotentialWinner();
  const potentialLoser = getPotentialLoser();
  const isCourt1 = match.court === 1;
  const isBottomCourt = match.court === 8; // Assuming 8 courts max for simplicity

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Enter Match Score
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <div className="text-center mb-4">
            <div
              className={`text-lg font-semibold mb-2 ${
                isCourt1 ? "text-yellow-700" : "text-gray-900"
              }`}
            >
              Court {match.court}{" "}
              {isCourt1 && <Crown className="inline w-5 h-5 ml-1" />}
            </div>
            <div className="text-sm text-gray-600">
              Round {match.round}
              {isCourt1 && (
                <div className="text-yellow-600 font-medium">
                  👑 Court 1 Battle!
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Team 1 Score */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {match.team1.name} Score
              </label>
              <div className="text-xs text-gray-600 mb-1">
                {match.team1.player1} & {match.team1.player2} •{" "}
                {match.team1.wins}W - {match.team1.losses}L
              </div>
              <input
                type="number"
                min="0"
                max="13"
                value={team1Score}
                onChange={(e) => setTeam1Score(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0"
                required
              />
            </div>

            {/* VS */}
            <div className="text-center text-gray-500 font-medium">VS</div>

            {/* Team 2 Score */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {match.team2.name} Score
              </label>
              <div className="text-xs text-gray-600 mb-1">
                {match.team2.player1} & {match.team2.player2} •{" "}
                {match.team2.wins}W - {match.team2.losses}L
              </div>
              <input
                type="number"
                min="0"
                max="13"
                value={team2Score}
                onChange={(e) => setTeam2Score(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0"
                required
              />
            </div>

            {/* Potential Winner/Loser Display */}
            {potentialWinner && potentialLoser && (
              <div className="space-y-2">
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center">
                    <Trophy className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-800">
                      Winner: {potentialWinner.team.name}
                    </span>
                  </div>
                  <div className="flex items-center mt-1">
                    {isCourt1 ? (
                      <div className="flex items-center text-yellow-600">
                        <Crown className="w-4 h-4 mr-1" />
                        <span className="text-xs">Defends Court 1!</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-green-600">
                        <ArrowUp className="w-4 h-4 mr-1" />
                        <span className="text-xs">
                          Moves UP to Court {match.court - 1}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-red-800">
                      Loser: {potentialLoser.team.name}
                    </span>
                  </div>
                  <div className="flex items-center mt-1">
                    {isBottomCourt ? (
                      <div className="text-xs text-gray-600">
                        Stays on Court {match.court}
                      </div>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <ArrowDown className="w-4 h-4 mr-1" />
                        <span className="text-xs">
                          Moves DOWN to Court {match.court + 1}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-sm text-red-800">{error}</span>
                </div>
              </div>
            )}

            {/* Rules Reminder */}
            <div
              className={`border rounded-md p-3 ${
                isCourt1
                  ? "bg-yellow-50 border-yellow-200"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <div
                className={`text-sm ${
                  isCourt1 ? "text-yellow-800" : "text-blue-800"
                }`}
              >
                <strong>Classic Speed Ladder Rules:</strong>
                <ul className="mt-1 space-y-1">
                  <li>• First to 11 points wins</li>
                  <li>• Must win by 2 points</li>
                  <li>• Maximum score is 13 points</li>
                  <li>• Winners move UP, losers move DOWN</li>
                  {isCourt1 && (
                    <li>
                      • <strong>Court 1 winners defend the throne! 👑</strong>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!team1Score || !team2Score}
                className={`flex-1 px-4 py-2 rounded-md transition-colors duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed ${
                  isCourt1
                    ? "bg-yellow-600 text-white hover:bg-yellow-700"
                    : "bg-primary-600 text-white hover:bg-primary-700"
                }`}
              >
                Submit Score
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClassicLadderScoreEntry;
